import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  Activity,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Target,
  ChevronDown,
  ChevronRight,
  Pause,
  TrendingUp,
  Filter,
} from 'lucide-react';
import {
  knowledgeFleetApi,
  queueControlApi,
  researchRunsApi,
  type LineageNode,
  type ResearchRunSummary,
} from '@/services/api';

type EventType =
  | 'task_scored'
  | 'task_dispatched'
  | 'task_completed'
  | 'task_deferred'
  | 'task_saturated'
  | 'task_failed'
  | 'weight_tuned';

interface ActivityEvent {
  id: string;
  type: EventType;
  at: string; // ISO timestamp
  research_type?: string | null;
  client_id?: string | null;
  target?: string | null;
  research_id?: string | null;
  // Type-specific fields
  priority_score?: number | null;
  entities_created?: number | null;
  actual_yield?: number | null;
  predicted_yield?: number | null;
  cost_usd?: number | null;
  duration_seconds?: number | null;
  reason?: string | null;
  decision?: string | null;
  error_message?: string | null;
  root_id?: string | null;
}

const POLL_INTERVAL_MS = 5_000;

function timeAgo(isoStr?: string | null): string {
  if (!isoStr) return '—';
  const d = new Date(isoStr);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 5) return 'just now';
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleString();
}

const TYPE_META: Record<
  EventType,
  { label: string; color: string; dot: string; Icon: React.ElementType }
> = {
  task_scored: {
    label: 'Scored',
    color: 'bg-blue-50 text-blue-700 border-blue-200',
    dot: 'bg-blue-400',
    Icon: Target,
  },
  task_dispatched: {
    label: 'Dispatched',
    color: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    dot: 'bg-indigo-400',
    Icon: Activity,
  },
  task_completed: {
    label: 'Completed',
    color: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    dot: 'bg-emerald-500',
    Icon: CheckCircle2,
  },
  task_deferred: {
    label: 'Deferred',
    color: 'bg-amber-50 text-amber-700 border-amber-200',
    dot: 'bg-amber-400',
    Icon: Pause,
  },
  task_saturated: {
    label: 'Saturated',
    color: 'bg-rose-50 text-rose-700 border-rose-200',
    dot: 'bg-rose-400',
    Icon: AlertTriangle,
  },
  task_failed: {
    label: 'Failed',
    color: 'bg-rose-50 text-rose-700 border-rose-200',
    dot: 'bg-rose-500',
    Icon: AlertTriangle,
  },
  weight_tuned: {
    label: 'Weights tuned',
    color: 'bg-violet-50 text-violet-700 border-violet-200',
    dot: 'bg-violet-400',
    Icon: TrendingUp,
  },
};

function lineageStatusToEventType(status: string): EventType | null {
  switch (status) {
    case 'completed':
      return 'task_completed';
    case 'deferred':
      return 'task_deferred';
    case 'saturated':
      return 'task_saturated';
    case 'failed':
      return 'task_failed';
    case 'dispatched':
      return 'task_dispatched';
    default:
      return null;
  }
}

function lineageNodeToEvent(n: LineageNode): ActivityEvent | null {
  const type = lineageStatusToEventType(n.status);
  if (!type) return null;
  const at = n.completed_at ?? n.started_at ?? n.created_at ?? new Date().toISOString();
  return {
    id: `lineage:${n.id}`,
    type,
    at,
    research_type: n.research_type,
    client_id: n.client_id,
    target: n.target_entity_name ?? n.target_entity_type ?? undefined,
    entities_created: n.entities_created,
    actual_yield: n.actual_yield,
    predicted_yield: n.predicted_yield,
    cost_usd: n.cost_usd,
    duration_seconds: n.duration_seconds,
    decision: n.decision,
    research_id: n.node_type === 'research_run' ? n.id : null,
    root_id: n.root_id,
  };
}

function runToEvent(r: ResearchRunSummary): ActivityEvent | null {
  if (r.status !== 'completed' && r.status !== 'failed') return null;
  return {
    id: `run:${r.id}`,
    type: r.status === 'completed' ? 'task_completed' : 'task_failed',
    at: r.completed_at ?? r.started_at ?? r.created_at ?? new Date().toISOString(),
    research_type: r.research_type,
    client_id: r.client_id ?? undefined,
    entities_created: r.entities_created_count,
    cost_usd: r.cost_estimate,
    duration_seconds: r.duration_seconds,
    research_id: r.id,
    error_message: r.error_message,
  };
}

function pendingTaskToEvent(
  t: NonNullable<Awaited<ReturnType<typeof queueControlApi.getStatus>>['top_pending']>[number],
): ActivityEvent {
  return {
    id: `pending:${t.id}`,
    type: 'task_scored',
    at: new Date().toISOString(),
    research_type: t.research_type ?? null,
    client_id: t.client_id ?? null,
    target: t.target ?? null,
    priority_score: t.priority_score ?? null,
  };
}

const EVENT_TYPE_OPTIONS: Array<{ value: EventType | ''; label: string }> = [
  { value: '', label: 'All events' },
  { value: 'task_completed', label: 'Completed' },
  { value: 'task_dispatched', label: 'Dispatched' },
  { value: 'task_deferred', label: 'Deferred' },
  { value: 'task_saturated', label: 'Saturated' },
  { value: 'task_failed', label: 'Failed' },
  { value: 'task_scored', label: 'Scored' },
  { value: 'weight_tuned', label: 'Weight tuned' },
];

export function ActivityFeed({
  clientIdFilter,
  defaultCollapsed = false,
}: {
  clientIdFilter?: string;
  defaultCollapsed?: boolean;
}) {
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [typeFilter, setTypeFilter] = useState<EventType | ''>('');
  const [clientFilter, setClientFilter] = useState(clientIdFilter ?? '');
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const [lastSync, setLastSync] = useState<string | null>(null);
  const pollHandleRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Keep clientFilter in sync with a new clientIdFilter prop (e.g. page-level selector).
  useEffect(() => {
    if (clientIdFilter != null && clientIdFilter !== clientFilter) {
      setClientFilter(clientIdFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientIdFilter]);

  const fetchOnce = useMemo(
    () =>
      async (client?: string) => {
        const [statusRes, rootsRes, runsRes] = await Promise.allSettled([
          queueControlApi.getStatus(),
          knowledgeFleetApi.listLineageRoots({ client_id: client, limit: 50 }),
          researchRunsApi.list({
            client_uid: client || undefined,
            limit: 50,
          }),
        ]);

        const collected: ActivityEvent[] = [];

        if (statusRes.status === 'fulfilled') {
          for (const t of statusRes.value.top_pending ?? []) {
            if (client && t.client_id && t.client_id !== client) continue;
            collected.push(pendingTaskToEvent(t));
          }
        }

        if (rootsRes.status === 'fulfilled') {
          for (const node of rootsRes.value) {
            const ev = lineageNodeToEvent(node);
            if (ev) collected.push(ev);
          }
        }

        if (runsRes.status === 'fulfilled') {
          for (const r of runsRes.value.runs) {
            const ev = runToEvent(r);
            if (ev) collected.push(ev);
          }
        }

        // Dedupe by (type, research_id || id suffix) preferring lineage rows
        // (which carry yield data) over plain run rows.
        const seen = new Map<string, ActivityEvent>();
        for (const e of collected) {
          const key = e.research_id
            ? `${e.type}:${e.research_id}`
            : `${e.type}:${e.id}`;
          const existing = seen.get(key);
          if (!existing) {
            seen.set(key, e);
          } else if (e.actual_yield != null && existing.actual_yield == null) {
            seen.set(key, e);
          }
        }

        const sorted = Array.from(seen.values()).sort(
          (a, b) => (b.at ?? '').localeCompare(a.at ?? ''),
        );
        setEvents(sorted.slice(0, 100));
        setLastSync(new Date().toISOString());
      },
    [],
  );

  useEffect(() => {
    fetchOnce(clientFilter || undefined);
    pollHandleRef.current = setInterval(() => {
      fetchOnce(clientFilter || undefined);
    }, POLL_INTERVAL_MS);
    return () => {
      if (pollHandleRef.current) clearInterval(pollHandleRef.current);
    };
  }, [fetchOnce, clientFilter]);

  const filtered = useMemo(() => {
    if (!typeFilter) return events;
    return events.filter((e) => e.type === typeFilter);
  }, [events, typeFilter]);

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
      <button
        onClick={() => setCollapsed((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-3 hover:bg-slate-50 transition-colors rounded-t-xl"
      >
        <div className="flex items-center gap-2">
          {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
          <Activity size={16} className="text-brand-teal" />
          <h2 className="font-semibold text-navy-900 text-sm">Activity Feed</h2>
          <span className="text-[10px] font-mono text-slate-400">
            ({filtered.length})
          </span>
        </div>
        <span className="text-[10px] text-slate-400">
          {lastSync ? `synced ${timeAgo(lastSync)}` : '—'}
        </span>
      </button>

      {!collapsed && (
        <>
          <div className="flex items-center gap-2 px-5 py-2 border-t border-slate-100 bg-slate-50">
            <Filter size={11} className="text-slate-400" />
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter((e.target.value || '') as EventType | '')}
              className="text-xs bg-white border border-slate-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-brand-teal"
            >
              {EVENT_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Client ID"
              value={clientFilter}
              onChange={(e) => setClientFilter(e.target.value)}
              className="text-xs bg-white border border-slate-200 rounded px-2 py-1 w-40 focus:outline-none focus:ring-1 focus:ring-brand-teal"
            />
            {(typeFilter || clientFilter !== (clientIdFilter ?? '')) && (
              <button
                onClick={() => {
                  setTypeFilter('');
                  setClientFilter(clientIdFilter ?? '');
                }}
                className="text-[10px] text-slate-500 hover:text-slate-700"
              >
                Clear
              </button>
            )}
          </div>

          <div className="max-h-[32rem] overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-5 py-8 text-center text-sm text-slate-400">
                No activity yet. The feed polls every {POLL_INTERVAL_MS / 1000}s.
              </div>
            ) : (
              filtered.map((e) => <ActivityRow key={e.id} event={e} />)
            )}
          </div>
        </>
      )}
    </div>
  );
}

function ActivityRow({ event }: { event: ActivityEvent }) {
  const navigate = useNavigate();
  const meta = TYPE_META[event.type];
  const Icon = meta.Icon;

  const handleClick = () => {
    if (event.research_id) {
      navigate(`/knowledge/estate/runs/${event.research_id}`);
    } else if (event.root_id) {
      navigate(`/knowledge/estate/lineage/${event.root_id}`);
    } else if (event.client_id) {
      navigate(
        `/knowledge/estate/runs?client_uid=${encodeURIComponent(event.client_id)}`,
      );
    }
  };

  const yieldDelta =
    event.actual_yield != null && event.predicted_yield != null
      ? event.actual_yield - event.predicted_yield
      : null;

  return (
    <button
      onClick={handleClick}
      className="w-full text-left flex items-start gap-3 px-5 py-2.5 border-b border-slate-100 hover:bg-slate-50 transition-colors"
    >
      <span
        className={clsx('mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0', meta.dot)}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span
            className={clsx(
              'text-[10px] font-bold px-1.5 py-0.5 rounded-full border flex items-center gap-1',
              meta.color,
            )}
          >
            <Icon size={10} />
            {meta.label}
          </span>
          {event.research_type && (
            <span className="text-xs text-slate-700 font-medium truncate">
              {event.research_type}
            </span>
          )}
          {event.target && (
            <span className="text-xs text-slate-500 truncate">
              {event.target}
            </span>
          )}
          <span className="ml-auto flex items-center gap-1 text-[10px] text-slate-400">
            <Clock size={9} />
            {timeAgo(event.at)}
          </span>
        </div>
        <div className="mt-1 flex items-center gap-3 text-[10px] text-slate-500 pl-0.5">
          {event.client_id && (
            <span className="text-navy-900 font-medium">{event.client_id}</span>
          )}
          {event.type === 'task_scored' && event.priority_score != null && (
            <span>priority {(event.priority_score * 100).toFixed(0)}%</span>
          )}
          {event.type === 'task_completed' && event.entities_created != null && (
            <span>{event.entities_created} entities</span>
          )}
          {event.cost_usd != null && event.cost_usd > 0 && (
            <span>${event.cost_usd.toFixed(3)}</span>
          )}
          {yieldDelta != null && (
            <span
              className={clsx(
                'font-medium',
                yieldDelta >= 0 ? 'text-emerald-600' : 'text-rose-600',
              )}
            >
              yield Δ {(yieldDelta * 100).toFixed(1)}%
            </span>
          )}
          {event.type === 'task_deferred' && event.decision && (
            <span className="uppercase font-medium">{event.decision}</span>
          )}
          {event.error_message && (
            <span className="text-rose-600 truncate">{event.error_message}</span>
          )}
        </div>
      </div>
    </button>
  );
}
